import { useEffect, useState } from "react";
import { PlatformApiClient, PlatformApiError } from "../api";
import { ActionBar, StatusState } from "../adaptive/primitives";
import { interpretMenuAgentMessage } from "./interpret";
import type { MenuAgentContext } from "./types";

type AgentMessage = { id: number; role: "user" | "agent"; text: string; proposalId?: string; state?: "proposal" | "clarify" | "done" | "error" };
let nextId = 1;

export function MenuAgentView({ slug, api, onExpired }: { slug: string; api: PlatformApiClient; onExpired: () => void }) {
  const [context, setContext] = useState<MenuAgentContext | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  function expired(cause: unknown): boolean {
    if (cause instanceof PlatformApiError && cause.status === 401) { onExpired(); return true; }
    return false;
  }

  useEffect(() => {
    setConversationId(null);
    setMessages([]);
    api.menu<MenuAgentContext>(slug, "/agent").then((loaded) => {
      setContext(loaded);
      setLoadError(null);
    }).catch((cause) => {
      if (!expired(cause)) setLoadError(cause instanceof PlatformApiError && cause.status === 403 ? "No tenés permisos para usar el agente de esta carta." : "No pudimos abrir el agente de Menu.");
    });
  }, [slug]);

  function push(message: Omit<AgentMessage, "id">) { setMessages((current) => [...current, { id: nextId++, ...message }]); }

  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (!draft.trim() || !context) return;
    const text = draft.trim(); setDraft(""); push({ role: "user", text }); setBusy(true);
    try {
      let outgoing = text; let summary = text;
      if (!context.aiEnabled) {
        const interpreted = interpretMenuAgentMessage(text, context);
        if (interpreted.error || !interpreted.composedMessage) { push({ role: "agent", text: interpreted.error ?? "No entendí el cambio.", state: "error" }); return; }
        outgoing = interpreted.composedMessage; summary = interpreted.summary ?? text;
      }
      const result = await api.menu<any>(slug, "/agent/messages", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ message: outgoing, inputType: "text", conversationId }) });
      if (typeof result.conversationId === "string" && result.conversationId) setConversationId(result.conversationId);
      if (result.notice) push({ role: "agent", text: result.notice });
      if (result.status === "proposed") push({ role: "agent", text: result.proposal?.summary ?? summary, proposalId: result.proposalId, state: "proposal" });
      else if (result.status === "clarify") push({ role: "agent", text: result.question ?? "Necesito una aclaración.", state: "clarify" });
      else if (result.status === "preview") push({ role: "agent", text: result.previewHref ? `Vista previa: ${result.previewHref}` : "Vista previa lista." });
      else push({ role: "agent", text: result.message ?? result.reason ?? "Listo." });
    } catch (cause) {
      if (!expired(cause)) push({ role: "agent", text: "No pudimos procesar ese cambio.", state: "error" });
    } finally { setBusy(false); }
  }

  async function proposal(message: AgentMessage, action: "confirm" | "cancel") {
    if (!message.proposalId) return;
    try {
      const result = await api.menu<{ message?: string }>(slug, `/agent/proposals/${encodeURIComponent(message.proposalId)}/${action}`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
      setMessages((current) => current.map((item) => item.id === message.id ? { ...item, text: result.message ?? (action === "confirm" ? "Cambio aplicado." : "Cambio cancelado."), state: "done" } : item));
    } catch (cause) {
      if (!expired(cause)) setMessages((current) => current.map((item) => item.id === message.id ? { ...item, text: "No pudimos completar la acción.", state: "error" } : item));
    }
  }

  if (loadError) return <StatusState className="menu-state" tone="danger" live="assertive" title="No pudimos abrir el agente" message={loadError} />;
  if (!context) return <StatusState className="menu-state" tone="loading" title="Cargando agente…" message="Estamos preparando el contexto de tu carta." />;

  return <div className="menu-agent">
    <div className="menu-agent-status"><strong>{context.status?.businessName ?? "Agente de tu carta"}</strong><span>{context.status?.hasPendingChanges ? "Hay cambios pendientes" : "Carta al día"} · {context.status?.unavailableCount ?? 0} agotados</span></div>
    {context.translationNotice && <div className="menu-notice">{context.translationNotice}</div>}
    <div className="menu-chat" aria-live="polite">
      {messages.length === 0
        ? <StatusState className="menu-empty" title="Hola, soy el agente de tu carta" message="Pedime cambios de precio o disponibilidad con lenguaje natural." />
        : messages.map((message) => <div className={`menu-message ${message.role}`} key={message.id}>
          <p>{message.text}</p>
          {message.state === "proposal" && <ActionBar className="menu-row-actions" sticky="never" align="start"><button className="primary-button" onClick={() => void proposal(message, "confirm")}>Confirmar</button><button onClick={() => void proposal(message, "cancel")}>Cancelar</button></ActionBar>}
        </div>)}
    </div>
    <form className="menu-agent-composer" onSubmit={send}>
      <textarea aria-label="Mensaje para el agente" rows={2} placeholder="Contame qué querés cambiar en tu carta…" value={draft} onChange={(event) => setDraft(event.target.value)} />
      <button className="primary-button" disabled={busy || !draft.trim()} type="submit">{busy ? "Enviando…" : "Enviar"}</button>
    </form>
  </div>;
}
